import type { ReactNode } from "react";
import { Box, Stack, Typography, Button } from "@mui/material";

// ---- Props -----------------------------------------------------------------

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  /** `contained` (default) for the primary action, `outlined` for a
   *  secondary one. */
  variant?: "contained" | "outlined";
}

interface EmptyStateProps {
  /** Icon shown in the rounded badge above the title. Pass an MUI
   *  icon element — it inherits the badge's sizing. */
  icon?: ReactNode;
  /** Short headline — what's missing / why the area is empty. */
  title: string;
  /** Optional supporting sentence. */
  body?: string;
  /** Action buttons. The first is rendered as the primary CTA; pass a
   *  second for an alternative path (e.g. "Build a Bot" + "Trade"). */
  actions?: EmptyStateAction[];
}

// ---- Component -------------------------------------------------------------

/**
 * Centered empty-state card — shown when a list/filter yields nothing.
 *
 * Design brief (Sergey 2026-05-13): "if nothing to show because of
 * filters, add a central button/card with action relevant to that
 * screen." Shared so My Bots / Marketplace / Testing / Lists all get
 * the same treatment: an icon badge, a one-line headline, optional
 * body copy, and one or two action buttons that move the user
 * forward (Build a Bot, Trade, Browse the library, …).
 *
 * Deliberately flat — no card border/elevation — to honour the
 * "maximise space, avoid card chrome" principle. The visual weight
 * comes from the icon badge and the CTA, not a panel.
 */
export function EmptyState({ icon, title, body, actions }: EmptyStateProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        // Generous vertical room so the state reads as intentional
        // rather than a glitch, but not so tall it pushes the FAB.
        py: { xs: 6, md: 8 },
        px: 3,
        gap: 2
      }}
    >
      {icon && (
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "action.hover",
            color: "text.secondary",
            "& svg": { fontSize: 30 }
          }}
        >
          {icon}
        </Box>
      )}

      <Stack spacing={0.5} sx={{ maxWidth: 360 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {body && (
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        )}
      </Stack>

      {actions && actions.length > 0 && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ mt: 1, flexWrap: "wrap", justifyContent: "center" }}
        >
          {actions.map((a, i) => (
            <Button
              key={a.label}
              variant={a.variant ?? (i === 0 ? "contained" : "outlined")}
              onClick={a.onClick}
            >
              {a.label}
            </Button>
          ))}
        </Stack>
      )}
    </Box>
  );
}
